import styled from 'styled-components';
import { Skeleton } from '../Skeleton';
import { colors, radii, spacing } from '../../theme-v2';

export interface ChatLoadingStateProps {
  className?: string;
}

/** Matches `SuggestedPromptChip`'s rendered height (10px padding + border). */
const CHIP_HEIGHT = 43;

/**
 * ChatLoadingState — placeholder shown inside `<ChatThread>` while the chatbot
 * completes its handshake. The backend's first frame carries both the greeting
 * and the initial suggested questions, so this traces the shape of that frame:
 * an assistant bubble at the top, a row of two prompt-chip placeholders at the
 * bottom.
 *
 * Purely decorative (`aria-hidden`) — the surrounding `<ChatThread>` is an
 * `aria-live` log, so assistive tech announces the real greeting the moment it
 * replaces this. Nothing here is copy, which also keeps the lib translation-free.
 */
export const ChatLoadingState = ({ className }: ChatLoadingStateProps) => (
  <StyledWrapper className={className} aria-hidden="true">
    <StyledGreeting>
      <Skeleton variant="circular" width={32} height={32} />
      <StyledBubble>
        <Skeleton variant="text" rows={3} height={12} />
      </StyledBubble>
    </StyledGreeting>
    <StyledChipRow>
      <Skeleton
        variant="rectangular"
        height={CHIP_HEIGHT}
        borderRadius={radii.lg}
      />
      <Skeleton
        variant="rectangular"
        height={CHIP_HEIGHT}
        borderRadius={radii.lg}
      />
    </StyledChipRow>
  </StyledWrapper>
);

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  /* Mirrors ChatEmptyState so the two states occupy the same box — a failed
     handshake swaps one for the other without the panel shifting. */
  padding: ${spacing.lg}px ${spacing.md}px;
`;

const StyledGreeting = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing.sm}px;
  /* Takes the free space so the chip row stays pinned to the bottom. */
  flex: 1;
  min-height: 0;
`;

const StyledBubble = styled.div`
  flex: 1;
  max-width: 80%;
  display: flex;
  flex-direction: column;
  gap: ${spacing.sm}px;
  padding: ${spacing.md}px;
  border-radius: ${radii.box}px;
  background: ${colors.surface.muted};
`;

const StyledChipRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${spacing.sm}px;
  width: 100%;
`;
